import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  IconButton,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  User,
  listUsers,
  assignTeachersToStudent,
  getStudentTeachers,
} from '../../services/userService';

interface AssignTeachersDialogProps {
  open: boolean;
  student: User | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

/**
 * Dialog for assigning teachers to a student
 */
const AssignTeachersDialog: React.FC<AssignTeachersDialogProps> = ({
  open,
  student,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTeachers, setLoadingTeachers] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<string[]>([]);

  // Load teachers when dialog opens
  useEffect(() => {
    if (open && student) {
      loadTeachers();
      loadCurrentAssignments();
    }
  }, [open, student]);

  const loadTeachers = async () => {
    try {
      setLoadingTeachers(true);
      // Get all teachers from the same school
      const response = await listUsers({
        role: 'teacher',
        limit: 100, // Get all teachers
      });
      setTeachers(response.users);
    } catch (err: any) {
      setError(err.message || 'Failed to load teachers');
    } finally {
      setLoadingTeachers(false);
    }
  };

  const loadCurrentAssignments = async () => {
    if (!student) return;

    try {
      const response = await getStudentTeachers(student.user_id);
      const currentTeacherIds = response.teachers.map((t) => t.user_id);
      setSelectedTeacherIds(currentTeacherIds);
    } catch (err: any) {
      // If no teachers assigned yet, that's okay
      console.log('No teachers assigned yet');
    }
  };

  const handleTeacherChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedTeacherIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSubmit = async () => {
    if (!student) return;

    try {
      setLoading(true);
      setError(null);

      await assignTeachersToStudent(student.user_id, selectedTeacherIds);

      onSuccess(`Successfully assigned ${selectedTeacherIds.length} teacher(s) to ${student.name}`);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to assign teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedTeacherIds([]);
    setError(null);
    onClose();
  };

  const getTeacherName = (teacherId: string): string => {
    const teacher = teachers.find((t) => t.user_id === teacherId);
    return teacher ? teacher.name : teacherId;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign Teachers to {student?.name}
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loadingTeachers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="teachers-label">Select Teachers</InputLabel>
              <Select
                labelId="teachers-label"
                multiple
                value={selectedTeacherIds}
                onChange={handleTeacherChange}
                input={<OutlinedInput label="Select Teachers" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={getTeacherName(value)} size="small" />
                    ))}
                  </Box>
                )}
                disabled={loading}
              >
                {teachers.length === 0 ? (
                  <MenuItem disabled>No teachers available</MenuItem>
                ) : (
                  teachers.map((teacher) => (
                    <MenuItem key={teacher.user_id} value={teacher.user_id}>
                      {teacher.name} ({teacher.email})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 2 }}>
              Select one or more teachers to assign to this student. You can assign multiple
              teachers if the student has classes with different teachers.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || loadingTeachers}>
          {loading ? 'Assigning...' : 'Assign Teachers'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignTeachersDialog;
